import { HexColorPicker } from 'react-colorful'
import { useState } from 'react'
import CommonSettings from './CommonSettings'
import RichTextEditor from './RichTextEditor'

export default function TextSettings({ block, onUpdate }) {
  const { settings } = block
  const [showColorPicker, setShowColorPicker] = useState(false)

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
      <h4 style={{ margin: 0, fontSize: '0.9rem', fontWeight: '600' }}>Text Content</h4>

      <div>
        <label style={{ fontSize: '0.85rem', fontWeight: '500', display: 'block', marginBottom: '0.5rem' }}>
          Content
        </label>
        <RichTextEditor
          value={settings.content || ''}
          onChange={(html) => onUpdate({ content: html })}
          placeholder="Type your text content here..."
        />
      </div>

      <h4 style={{ margin: 0, fontSize: '0.9rem', fontWeight: '600' }}>Typography</h4>

      <div>
        <label style={{ fontSize: '0.85rem', fontWeight: '500', display: 'block', marginBottom: '0.5rem' }}>
          Font Size
        </label>
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
          <input
            type="range"
            min="10"
            max="48"
            value={parseInt(settings.fontSize) || 16}
            onChange={(e) => onUpdate({ fontSize: `${e.target.value}px` })}
            style={{ flex: 1 }}
          />
          <span style={{ fontSize: '0.75rem', color: '#64748b', minWidth: '36px', textAlign: 'right' }}>
            {parseInt(settings.fontSize) || 16}px
          </span>
        </div>
      </div>

      <div>
        <label style={{ fontSize: '0.85rem', fontWeight: '500', display: 'block', marginBottom: '0.5rem' }}>
          Font Family
        </label>
        <select
          value={settings.fontFamily || 'Arial, sans-serif'}
          onChange={(e) => onUpdate({ fontFamily: e.target.value })}
          style={{ width: '100%', fontSize: '0.85rem', padding: '0.4rem' }}
        >
          <option value="Arial, sans-serif">Arial</option>
          <option value="Georgia, serif">Georgia</option>
          <option value="'Times New Roman', serif">Times New Roman</option>
          <option value="'Courier New', monospace">Courier New</option>
          <option value="Verdana, sans-serif">Verdana</option>
          <option value="Tahoma, sans-serif">Tahoma</option>
          <option value="'Trebuchet MS', sans-serif">Trebuchet MS</option>
          <option value="Helvetica, sans-serif">Helvetica</option>
        </select>
      </div>

      <div>
        <label style={{ fontSize: '0.85rem', fontWeight: '500', display: 'block', marginBottom: '0.5rem' }}>
          Font Weight
        </label>
        <div style={{ display: 'flex', gap: '0.35rem' }}>
          {[
            { value: 'lighter', label: 'Light' },
            { value: 'normal', label: 'Regular' },
            { value: 'bold', label: 'Bold' }
          ].map(opt => (
            <button
              key={opt.value}
              onClick={() => onUpdate({ fontWeight: opt.value })}
              style={{
                flex: 1,
                padding: '0.35rem',
                fontSize: '0.75rem',
                fontWeight: opt.value === 'bold' ? '700' : opt.value === 'lighter' ? '300' : '400',
                border: `1.5px solid ${settings.fontWeight === opt.value ? 'var(--color-primary)' : '#e2e8f0'}`,
                borderRadius: '6px',
                background: settings.fontWeight === opt.value ? 'rgba(99,102,241,0.06)' : '#fff',
                color: settings.fontWeight === opt.value ? 'var(--color-primary)' : '#475569',
                cursor: 'pointer'
              }}
            >
              {opt.label}
            </button>
          ))}
        </div>
      </div>

      <div style={{ position: 'relative' }}>
        <label style={{ fontSize: '0.85rem', fontWeight: '500', display: 'block', marginBottom: '0.5rem' }}>
          Text Color
        </label>
        <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
          <div
            onClick={() => setShowColorPicker(!showColorPicker)}
            style={{
              width: '36px',
              height: '32px',
              backgroundColor: settings.color || '#333333',
              border: '1px solid var(--color-border)',
              borderRadius: '6px',
              cursor: 'pointer',
              boxShadow: 'inset 0 0 0 1px rgba(0,0,0,0.05)'
            }}
          />
          <input
            type="text"
            value={settings.color || ''}
            onChange={(e) => onUpdate({ color: e.target.value })}
            onBlur={(e) => {
              if (!e.target.value.trim()) {
                onUpdate({ color: '#333333' })
              }
            }}
            placeholder="#333333"
            style={{ flex: 1, fontSize: '0.85rem', padding: '0.4rem' }}
          />
        </div>
        {showColorPicker && (
          <div style={{ position: 'absolute', zIndex: 10, marginTop: '0.5rem' }}>
            <div
              style={{ position: 'fixed', inset: 0 }}
              onClick={() => setShowColorPicker(false)}
            />
            <HexColorPicker
              color={settings.color || '#333333'}
              onChange={(color) => onUpdate({ color })}
            />
          </div>
        )}
      </div>

      <div>
        <label style={{ fontSize: '0.85rem', fontWeight: '500', display: 'block', marginBottom: '0.5rem' }}>
          Text Align
        </label>
        <div style={{ display: 'flex', gap: '0.35rem' }}>
          {['left', 'center', 'right', 'justify'].map(align => (
            <button
              key={align}
              onClick={() => onUpdate({ textAlign: align })}
              style={{
                flex: 1,
                padding: '0.4rem',
                fontSize: '0.7rem',
                textTransform: 'capitalize',
                border: `1.5px solid ${settings.textAlign === align ? 'var(--color-primary)' : '#e2e8f0'}`,
                borderRadius: '6px',
                background: settings.textAlign === align ? 'rgba(99,102,241,0.06)' : '#fff',
                color: settings.textAlign === align ? 'var(--color-primary)' : '#475569',
                cursor: 'pointer',
                fontWeight: settings.textAlign === align ? '600' : '400'
              }}
            >
              {align}
            </button>
          ))}
        </div>
      </div>

      <div>
        <label style={{ fontSize: '0.85rem', fontWeight: '500', display: 'block', marginBottom: '0.5rem' }}>
          Line Height
        </label>
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
          <input
            type="range"
            min="1"
            max="3"
            step="0.1"
            value={parseFloat(settings.lineHeight) || 1.6}
            onChange={(e) => onUpdate({ lineHeight: e.target.value })}
            style={{ flex: 1 }}
          />
          <span style={{ fontSize: '0.75rem', color: '#64748b', minWidth: '30px', textAlign: 'right' }}>
            {parseFloat(settings.lineHeight) || 1.6}
          </span>
        </div>
      </div>

      <CommonSettings settings={settings} onUpdate={onUpdate} />
    </div>
  )
}
